import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useState, type ReactElement } from 'react'

import { MobileSectionSwitcher } from '~/components/navigation/MobileSectionSwitcher'
import {
  AddonPricingMatrix,
  CatalogEditor,
  SyncStripeButton,
  catalogEntryQuery,
  useCreateCatalogEntry,
  useUpdateCatalogEntry,
} from '~/features/addons'
import { logger } from '~/lib/logger'

/**
 * `/admin/subscriptions/addons/$addonKey` — catalog row editor.
 *
 * `addonKey === 'new'` → create mode (no fetch, blank form).
 * Any other value → fetch + edit. The same form component handles both.
 *
 * Layout: left column = Details form (CatalogEditor). Right column stacks
 * the Pricing matrix and Stripe sync card. On create the right column is
 * hidden until the row exists (no addon_key to scope prices/sync against).
 *
 * Mirrors the plan-editor route structure for consistency.
 */
export const Route = createFileRoute('/admin/subscriptions/addons/$addonKey')({
  component: AddonEditorPage,
})

type Tab = 'details' | 'pricing' | 'sync'

function AddonEditorPage(): ReactElement {
  const { addonKey } = Route.useParams()
  const isCreate = addonKey === 'new'
  const navigate = useNavigate()

  const entry = useQuery(catalogEntryQuery(isCreate ? '' : addonKey))
  const createMutation = useCreateCatalogEntry()
  const updateMutation = useUpdateCatalogEntry()

  const [tab, setTab] = useState<Tab>('details')

  if (!isCreate && entry.isLoading) return <Skeleton />
  if (!isCreate && !entry.data && entry.isSuccess) {
    return (
      <div className="ka-subs-list-page ka-subs-editor-page">
        <div className="ka-card ka-subs-empty-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Add-on not found</div>
            <div className="ka-empty-body">
              The key <code>{addonKey}</code> is not in the catalog. It may have been deleted
              or renamed.
            </div>
            <div className="ka-empty-actions">
              <Link to="/admin/subscriptions/addons" className="ka-btn ka-btn--ghost">
                Back to add-ons
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const initial = isCreate ? undefined : entry.data
  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (
    payload:
      | { mode: 'create'; values: Parameters<typeof createMutation.mutate>[0] }
      | {
          mode: 'edit'
          key: string
          values: Parameters<typeof updateMutation.mutate>[0]['patch']
        },
  ): void => {
    if (payload.mode === 'create') {
      createMutation.mutate(payload.values, {
        onSuccess: (created) => {
          void navigate({
            to: '/admin/subscriptions/addons/$addonKey',
            params: { addonKey: created.key },
          })
        },
        onError: (cause) => logger.warn('addon create failed', { cause }),
      })
    } else {
      updateMutation.mutate(
        { key: payload.key, patch: payload.values },
        {
          onSuccess: () => {
            // Stay on detail; refetch handles displayed values.
          },
          onError: (cause) => logger.warn('addon update failed', { cause }),
        },
      )
    }
  }

  return (
    <div className="ka-subs-list-page ka-subs-editor-page">
      <header className="ka-page-header">
        <div>
          <nav aria-label="Breadcrumb" className="ka-breadcrumb">
            <Link to="/admin/subscriptions/addons" className="ka-breadcrumb-item">
              Add-ons
            </Link>
            <ChevronRight size={12} aria-hidden="true" className="ka-breadcrumb-sep" />
            <span className="ka-breadcrumb-item">
              {isCreate ? 'New add-on' : (initial?.display_name ?? addonKey)}
            </span>
          </nav>
          <h1 className="ka-page-header-title">
            {isCreate ? 'New add-on' : (initial?.display_name ?? addonKey)}
          </h1>
        </div>
      </header>

      {!isCreate && initial ? (
        <>
          <MobileSectionSwitcher
            label="Add-on editor section"
            value={tab}
            options={[
              { value: 'details', label: 'Details' },
              { value: 'pricing', label: 'Pricing' },
              { value: 'sync', label: 'Stripe sync' },
            ]}
            onChange={(value) => setTab(value as Tab)}
          />
          <div className="ka-tabbar" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'details'}
              className={`ka-tabbar-tab${tab === 'details' ? ' is-active' : ''}`}
              onClick={() => setTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'pricing'}
              className={`ka-tabbar-tab${tab === 'pricing' ? ' is-active' : ''}`}
              onClick={() => setTab('pricing')}
            >
              Pricing
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'sync'}
              className={`ka-tabbar-tab${tab === 'sync' ? ' is-active' : ''}`}
              onClick={() => setTab('sync')}
            >
              Stripe sync
            </button>
          </div>
        </>
      ) : null}

      {isCreate || tab === 'details' ? (
        <CatalogEditor
          initial={initial}
          isSubmitting={isSubmitting}
          onCancel={() => void navigate({ to: '/admin/subscriptions/addons' })}
          onSubmit={handleSubmit}
        />
      ) : null}

      {!isCreate && initial && tab === 'pricing' ? (
        <AddonPricingMatrix catalog={initial} />
      ) : null}

      {!isCreate && initial && tab === 'sync' ? <SyncStripeButton catalog={initial} /> : null}
    </div>
  )
}

function Skeleton(): ReactElement {
  return (
    <div className="ka-subs-list-page ka-subs-editor-page" aria-busy="true">
      <div className="ka-card ka-card-body--lg ka-subs-empty-card">
        <div className="ka-sk ka-sk--heading ka-sk--mb" />
        <div className="ka-sk ka-sk--tall" />
      </div>
    </div>
  )
}
